// src/utils/openInApp.js
//
// Shared helper to open the CBTHost Android app from a web page.
//
// The flow:
//   1) Try the verified HTTPS App Link first
//   2) Fall back to the custom cbthost:// scheme
//   3) Fall back to the Play Store (Android) / App Store (iOS)
//
// The app's manifest is set up to receive:
//   https://app.cbthost.com/participate/verification?cbt_pass_code=XXX
//   https://quizbank.cbthost.com/participate/verification?cbt_pass_code=XXX
//   cbthost://verification?cbt_pass_code=XXX

export const ANDROID_PACKAGE = 'com.elite_stack.cbthost';
export const PLAY_STORE_URL =
  `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
export const APP_STORE_URL = 'https://apps.apple.com/app/cbthost/id0000000000'; // replace later

const PRIMARY_APP_LINK_HOST = 'app.cbthost.com';
const SECONDARY_APP_LINK_HOST = 'quizbank.cbthost.com';

/**
 * Returns one of: 'android' | 'ios' | 'desktop'
 */
export function getPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || navigator.vendor || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  return 'desktop';
}

/**
 * Builds the URLs we'll try, in order.
 */
function buildUrls(passcode, testId) {
  const qs = new URLSearchParams();
  qs.set('cbt_pass_code', passcode);
  if (testId) qs.set('test_id', testId);

  const queryString = qs.toString();

  return {
    appLinks: [
      `https://${PRIMARY_APP_LINK_HOST}/participate/verification?${queryString}`,
      `https://${SECONDARY_APP_LINK_HOST}/participate/verification?${queryString}`,
    ],
    customScheme: `cbthost://verification?${queryString}`,
  };
}

/**
 * Attempt to open the CBTHost app.
 *
 * If the app isn't installed, will fall through to the store (mobile only).
 *
 * @param {string} passcode  — required; the CBT pass code
 * @param {string} [testId]  — optional; for close_access tests
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs=1500] — how long to wait for the app to open
 * @param {boolean} [opts.storeFallback=true] — go to the store if app doesn't open
 * @param {boolean} [opts.forceScheme=false] — skip App Link, use cbthost:// directly
 */
export function openInApp(passcode, testId, opts = {}) {
  const {
    timeoutMs = 1500,
    storeFallback = true,
    forceScheme = false,
  } = opts;

  if (!passcode) {
    console.warn('openInApp: passcode is required');
    return;
  }

  const { appLinks, customScheme } = buildUrls(passcode, testId);
  const platform = getPlatform();

  // ── Desktop: nothing to do, just open the primary link in a new tab ──
  if (platform === 'desktop') {
    window.open(appLinks[0], '_blank', 'noopener,noreferrer');
    return;
  }

  // ── Mobile: try to open the app, fall back to store ──

  // We detect "app didn't open" by watching whether the page is still
  // visible after the timeout. When the app opens, the browser is
  // backgrounded and `document.hidden` becomes true (or the page fires
  // `pagehide`/`blur`).
  let didLeavePage = false;

  const onVisibilityChange = () => {
    if (document.hidden) didLeavePage = true;
  };
  const onPageHide = () => { didLeavePage = true; };
  const onBlur = () => { didLeavePage = true; };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('blur', onBlur);

  const cleanup = () => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('blur', onBlur);
  };

  // Try the first URL (App Link or custom scheme)
  const targetUrl = forceScheme ? customScheme : appLinks[0];

  // Using an iframe trick helps on some browsers where window.location
  // navigation inside an event handler gets blocked. But we also set
  // window.location as a fallback for the modern path.
  try {
    window.location.href = targetUrl;
  } catch (e) {
    console.warn('openInApp: location.href failed', e);
  }

  // Schedule the fallback
  const timer = setTimeout(() => {
    cleanup();
    if (didLeavePage) {
      // User left the page — the app opened. Nothing to do.
      return;
    }

    // The app didn't open. Try the custom scheme before the store.
    if (!forceScheme) {
      try {
        window.location.href = customScheme;
      } catch (_) {}

      // Then the store, if allowed
      setTimeout(() => {
        if (document.hidden) return;
        if (!storeFallback) return;
        if (platform === 'android') {
          window.location.href = PLAY_STORE_URL;
        } else if (platform === 'ios') {
          window.location.href = APP_STORE_URL;
        }
      }, timeoutMs);
    } else if (storeFallback) {
      if (platform === 'android') {
        window.location.href = PLAY_STORE_URL;
      } else if (platform === 'ios') {
        window.location.href = APP_STORE_URL;
      }
    }
  }, timeoutMs);

  // If we come back to the page (user pressed "back" from the store),
  // clear the pending timer so we don't navigate again.
  window.addEventListener('pageshow', () => {
    clearTimeout(timer);
    cleanup();
  });
}